import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { Box, TextField, Typography, MenuItem, Switch } from '@mui/material';
import HealtheraButton from 'app/shared-components/buttons/HealtheraButton';
import WorkdayHours from '../WorkdayHours';
import WorkdayHoursArray from '../WorkdayHoursArray';
import ClosureDays from './ClosureDays';
import api from '../../../../apis/core/pharmacy';
import hooks from '../../../pages/HealtheraPro/hooks';
import config from '../../../../config.json';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const DEFAULT_DAY_HOURS = { isOpened: false, open: null, close: null };
const DEFAULT_HOURS = Object.fromEntries(DAYS.map((day) => [day, DEFAULT_DAY_HOURS]));

const LEAD_TIME_OPTIONS = [
  { value: 0, label: '0 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 60, label: '1 hour' },
  { value: 90, label: '1 hour 30 minutes' },
  { value: 120, label: '2 hours' },
  { value: 150, label: '2 hours 30 minutes' },
  { value: 180, label: '3 hours' },
  { value: 210, label: '3 hours 30 minutes' },
  { value: 240, label: '4 hours' },
  { value: 270, label: '4 hours 30 minutes' },
  { value: 300, label: '5 hours' },
  { value: 330, label: '5 hours 30 minutes' },
  { value: 360, label: '6 hours' },
  { value: 390, label: '6 hours 30 minutes' },
  { value: 420, label: '7 hours' },
  { value: 450, label: '7 hours 30 minutes' },
  { value: 480, label: '8 hours' },
];

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PharmacyInfoForm = () => {
  const { selectedPharmacyId } = hooks.useGetSelectedPharmacyInfo();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: '',
      address: {
        addressLine1: '',
        addressLine2: '',
        city: '',
        county: '',
        postcode: '',
      },
      telephone: '',
      email: '',
      fax: '',
      orderFaxEnabled: true,
      welcomeMessage: '',
      hours: { ...DEFAULT_HOURS, workdays: [] },
      deliveryOrCollectLeadTime: 0,
    },
  });

  const {
    isLoading,
    data: pharmacyInfo,
    refetch,
  } = useQuery({
    queryKey: ['get-pharmacy', selectedPharmacyId],
    queryFn: () => api.getPharmacyById({ pharmacyId: selectedPharmacyId }),
    enabled: Boolean(selectedPharmacyId),
  });

  const pharmacy = pharmacyInfo?.pharmacy;

  useEffect(() => {
    if (pharmacy) {
      reset({
        name: pharmacy.name ?? '',
        address: {
          addressLine1: pharmacy.address?.addressLine1 ?? '',
          addressLine2: pharmacy.address?.addressLine2 ?? '',
          city: pharmacy.address?.city ?? '',
          county: pharmacy.address?.county ?? '',
          postcode: pharmacy.address?.postcode ?? '',
        },
        telephone: pharmacy.telephone ?? '',
        email: pharmacy.email ?? '',
        fax: pharmacy.fax ?? '',
        orderFaxEnabled: pharmacy.orderFaxEnabled ?? true,
        welcomeMessage: pharmacy.welcomeMessage ?? '',
        hours: {
          ...Object.fromEntries(
            DAYS.map((day) => [day, pharmacy.hours?.[day] ?? DEFAULT_DAY_HOURS])
          ),
          workdays: pharmacy.hours?.workdays ?? [],
        },
        deliveryOrCollectLeadTime: Number(pharmacy.deliveryOrCollectLeadTime) || 0,
      });
    }
  }, [pharmacy, reset]);

  const { mutate: mutatePharmacyInfo, isLoading: isUpdatingPharmacy } = useMutation({
    mutationFn: (data) =>
      api.updatePharmacyById({ pharmacyId: selectedPharmacyId, data }),
    onSuccess: async () => {
      enqueueSnackbar('Pharmacy details updated successfully', { variant: 'success' });
      await refetch();
    },
    onError: (error) => {
      console.error('Error updating pharmacy:', error);
      enqueueSnackbar('Pharmacy details could not be updated', { variant: 'error' });
    },
  });

  const onSubmit = (data) => {
    mutatePharmacyInfo(data);
  };

  if (isLoading || !pharmacy) {
    return <p>Loading...</p>;
  }

  return (
    <Box className="flex items-center justify-center p-16">
      <Box
        className="w-full max-w-[660px]"
        sx={{ border: '1px solid #ddd', borderRadius: '8px', padding: '16px' }}
      >
        <Typography variant="h6" gutterBottom className="font-bold mb-12">
          Pharmacy Settings
        </Typography>

        <Box component="form" onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-16">
          <Controller
            name="name"
            control={control}
            rules={{ required: 'Pharmacy name is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Pharmacy Name"
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.name}
                helperText={errors.name?.message}
              />
            )}
          />

          <Controller
            name="address.addressLine1"
            control={control}
            rules={{ required: 'Address line 1 is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Address line 1"
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.address?.addressLine1}
                helperText={errors.address?.addressLine1?.message}
              />
            )}
          />

          <Controller
            name="address.addressLine2"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Address line 2"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            )}
          />

          <Controller
            name="address.city"
            control={control}
            rules={{ required: 'City is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                label="City"
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.address?.city}
                helperText={errors.address?.city?.message}
              />
            )}
          />

          <Controller
            name="address.county"
            control={control}
            rules={{ required: 'Province is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Province"
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.address?.county}
                helperText={errors.address?.county?.message}
              />
            )}
          />

          <Controller
            name="address.postcode"
            control={control}
            rules={{ required: 'Postcode is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Postcode"
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.address?.postcode}
                helperText={errors.address?.postcode?.message}
              />
            )}
          />

          <Controller
            name="telephone"
            control={control}
            rules={{ required: 'Telephone is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Telephone"
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.telephone}
                helperText={errors.telephone?.message}
              />
            )}
          />

          <Controller
            name="email"
            control={control}
            rules={{
              required: 'Email is required',
              pattern: { value: emailRegex, message: 'Invalid email address' },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Email"
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.email}
                helperText={errors.email?.message}
              />
            )}
          />

          <Controller
            name="fax"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Fax"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            )}
          />

          <Controller
            name="welcomeMessage"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Patient Welcome Message"
                fullWidth
                multiline
                rows={4}
                InputLabelProps={{ shrink: true }}
                placeholder="Write a welcome message for your patients..."
              />
            )}
          />

          <Typography className="font-600 text-14 mt-8">Opening Hours</Typography>
          {DAYS.map((day) => (
            <Controller
              key={day}
              name={`hours.${day}`}
              control={control}
              rules={{
                validate: (value) => {
                  if (value?.isOpened && (!value.open || !value.close)) {
                    return 'Open and close times are required';
                  }
                  return true;
                },
              }}
              render={({ field, fieldState }) => (
                <WorkdayHours
                  name={day}
                  data={field.value}
                  onChange={field.onChange}
                  error={fieldState.error?.message}
                />
              )}
            />
          ))}

          <Controller
            name="hours.workdays"
            control={control}
            render={({ field }) => (
              <WorkdayHoursArray
                formData={field.value}
                onChange={field.onChange}
              />
            )}
          />

          {config.deploymentRegion === 'canada' && (
            <>
              <Typography className="font-600 text-14 mt-8">Closure Days</Typography>
              <ClosureDays />
            </>
          )}

          <Typography className="font-600 text-14 mt-8">Prescription Ordering Preferences</Typography>

          {config.deploymentRegion === 'canada' &&
            !pharmacy.propelIntegrationEnabled &&
            pharmacy.fax && (
              <Controller
                name="orderFaxEnabled"
                control={control}
                render={({ field }) => (
                  <Box className="flex items-center gap-8">
                    <Typography component="label" className="font-500 opacity-90">
                      Enable Order Fax
                    </Typography>
                    <Switch
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  </Box>
                )}
              />
            )}

          <Controller
            name="deliveryOrCollectLeadTime"
            control={control}
            rules={{ required: 'Lead time is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                select
                label="Pickup lead time"
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.deliveryOrCollectLeadTime}
                helperText={errors.deliveryOrCollectLeadTime?.message}
              >
                {LEAD_TIME_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />

          <HealtheraButton type="submit" disabled={isUpdatingPharmacy}>
            {isUpdatingPharmacy ? 'Saving...' : 'Save changes'}
          </HealtheraButton>
        </Box>
      </Box>
    </Box>
  );
};

export default PharmacyInfoForm;
